import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { XIcon } from '../../../components/ui/Icons';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';

interface TodoAutocompleteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (todoId: number) => void;
}

interface TodoRecord {
  todoId: number;
  todoDescr?: string | null;
  todoDueDate?: string | null;
  client?: { clientName?: string };
}

const TodoAutocomplete: React.FC<TodoAutocompleteProps> = ({ isOpen, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useSWR<TodoRecord[] | { data: TodoRecord[] }>(
    isOpen ? '/crm-todos' : null,
    fetcher
  );
  const todos = Array.isArray(data) ? data : (data as { data: TodoRecord[] })?.data || [];

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return todos.slice(0, 50);
    const term = searchTerm.toLowerCase();
    return todos
      .filter(
        (t) =>
          (t.todoDescr || '').toLowerCase().includes(term) ||
          (t.client?.clientName || '').toLowerCase().includes(term)
      )
      .slice(0, 50);
  }, [todos, searchTerm]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filtered]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((p) => (p < filtered.length - 1 ? p + 1 : p));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((p) => (p > 0 ? p - 1 : 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      e.preventDefault();
      onSelect(filtered[selectedIndex].todoId);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Link to Todo</ModalTitle>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" aria-label="Close">
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by description or client..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
          <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto" ref={listRef}>
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Loading todos...</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm ? 'No todos found.' : 'No todos available.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filtered.map((todo, index) => (
                  <div
                    key={todo.todoId}
                    onClick={() => {
                      onSelect(todo.todoId);
                      onClose();
                    }}
                    className={`p-4 cursor-pointer ${index === selectedIndex ? 'bg-indigo-50 border-l-4 border-indigo-500' : 'hover:bg-gray-50'}`}
                  >
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {todo.todoDescr || `Todo #${todo.todoId}`}
                    </p>
                    {(todo.todoDueDate || todo.client?.clientName) && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {[todo.todoDueDate, todo.client?.clientName].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TodoAutocomplete;
